'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { SessionContext } from './Session';
import { useRBACOptional } from '@/contexts/RBACContext';
import {
  fetchSubTenantMemberUserIds,
  resolveOrgIdForSubTenantMemberFetch,
} from '@/lib/subTenantMemberUserIds';
import { readActiveTenantScope } from '@/lib/activeTenantScope';

export type WorkflowMemberScopeValue = {
  /** Joined account + member key for SWR/effects (starts with `loading:` while fetching). */
  scopeSignal: string;
  /**
   * Comma-separated external user IDs for workflow API `member_user_ids`.
   * `undefined` = not in sub-tenant listing scope or still loading.
   * `''` = scoped but zero members (explicit empty list on API).
   */
  memberUserIds: string | undefined;
};

export const WorkflowMemberScopeContext = createContext<WorkflowMemberScopeValue>({
  scopeSignal: '',
  memberUserIds: undefined,
});

export function isWorkflowSubTenantMembersPending(scopeSignal: string): boolean {
  return scopeSignal.startsWith('loading:');
}

/**
 * Resolves sub-tenant member external IDs for automation workflow API calls (same RBAC pattern as QR).
 */
export default function WorkflowAxios({ children }: { children: ReactNode }) {
  const sessionContext = useContext(SessionContext);
  const user = sessionContext?.user || null;
  const rbac = useRBACOptional();

  const [subTenantMemberKey, setSubTenantMemberKey] = useState('');
  const aggregateMemberIdsRef = useRef<number[] | null>(null);

  const tenantScope = readActiveTenantScope();
  const listingSubAccountId =
    rbac?.effectiveListingSubAccountId ??
    (rbac?.isRootContextActive && rbac?.defaultAccountId != null ? rbac.defaultAccountId : null) ??
    rbac?.selectedSubAccountId ??
    tenantScope.activeAccountId ??
    tenantScope.rootListingAccountId ??
    null;
  const isImpersonating =
    typeof window !== 'undefined' && window.localStorage.getItem('impersonation') === 'true';
  const isSubTenantListingScope =
    (Boolean(listingSubAccountId) || Boolean(tenantScope.isAggregateSubTenantScope)) &&
    !isImpersonating;
  if (!isSubTenantListingScope) {
    aggregateMemberIdsRef.current = null;
  }

  const ownerUserIdRef = useRef<number | null>(null);
  ownerUserIdRef.current = rbac?.userContext?.owner_user_id
    ? Number(rbac.userContext.owner_user_id)
    : null;
  const defaultAccountIdRef = useRef<number | null>(null);
  defaultAccountIdRef.current = rbac?.defaultAccountId ?? null;

  useEffect(() => {
    if (!listingSubAccountId || isImpersonating) {
      aggregateMemberIdsRef.current = null;
      setSubTenantMemberKey('');
      return;
    }
    const subId = listingSubAccountId;
    const orgId = resolveOrgIdForSubTenantMemberFetch(rbac?.currentOrgId, user);
    aggregateMemberIdsRef.current = [];
    setSubTenantMemberKey(`loading:${subId}`);
    let cancelled = false;
    (async () => {
      try {
        const ids = await fetchSubTenantMemberUserIds(subId, {
          orgId,
          bypassCache: true,
        });
        // Include org owner so root-member direct login still sees owner-created workflows.
        const ownerExtId = ownerUserIdRef.current;
        const rootAccId = defaultAccountIdRef.current;
        if (
          ownerExtId != null &&
          Number.isFinite(ownerExtId) &&
          (subId === rootAccId || tenantScope.rootListingAccountId === subId) &&
          !ids.includes(ownerExtId)
        ) {
          ids.push(ownerExtId);
        }
        if (!cancelled) {
          aggregateMemberIdsRef.current = ids;
          const sorted = [...ids].sort((a, b) => a - b);
          setSubTenantMemberKey(`${subId}:${sorted.join(',')}`);
        }
      } catch {
        if (!cancelled) {
          aggregateMemberIdsRef.current = [];
          setSubTenantMemberKey(`${subId}:`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isImpersonating,
    listingSubAccountId,
    rbac?.currentOrgId,
    tenantScope.rootListingAccountId,
    user,
  ]);

  const subTenantMemberListReady =
    isSubTenantListingScope &&
    Boolean(subTenantMemberKey) &&
    !subTenantMemberKey.startsWith('loading:');

  const effectiveMemberKeyForSignal =
    !isSubTenantListingScope
      ? ''
      : !subTenantMemberKey || subTenantMemberKey.startsWith('loading:')
        ? `loading:${listingSubAccountId}`
        : subTenantMemberKey;

  const scopeSignal = useMemo(
    () =>
      [
        effectiveMemberKeyForSignal,
        String(listingSubAccountId ?? ''),
        String(rbac?.currentAccountId ?? ''),
        String(rbac?.defaultAccountId ?? ''),
      ].join('|'),
    [
      effectiveMemberKeyForSignal,
      listingSubAccountId,
      rbac?.currentAccountId,
      rbac?.defaultAccountId,
    ]
  );

  const memberUserIds = useMemo((): string | undefined => {
    if (!isSubTenantListingScope) return undefined;
    if (!subTenantMemberListReady) return undefined;
    return (aggregateMemberIdsRef.current ?? []).map(String).join(',');
  }, [isSubTenantListingScope, subTenantMemberListReady, subTenantMemberKey]);

  const value = useMemo(
    () => ({ scopeSignal, memberUserIds }),
    [scopeSignal, memberUserIds]
  );

  return (
    <WorkflowMemberScopeContext.Provider value={value}>{children}</WorkflowMemberScopeContext.Provider>
  );
}
